export class ApiError extends Error {
  errors: string[] | null = null
  fieldErrors: Record<string, string[]> | null = null
  code: string | null = null
  status: number
  statusText: string

  constructor(
    message: string,
    status: number,
    statusText: string,
    errors?: string[],
    fieldErrors?: Record<string, string[]>,
    code?: string
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.errors = errors || null
    this.fieldErrors = fieldErrors || null
    this.code = code || null
  }
}

export interface ErrorPayload {
  errors?: unknown
  fieldErrors?: unknown
  error?: unknown
  code?: unknown
}

const isCodedErrorEntry = (entry: unknown): entry is { code: string } => {
  if (!entry || typeof entry !== 'object') return false
  const { code } = entry as { code?: unknown }
  return typeof code === 'string' && code.length > 0
}

export const errorEntryText = (entry: unknown): string => {
  if (typeof entry === 'string') return entry
  if (isCodedErrorEntry(entry)) return entry.code
  return String(entry)
}

export const readErrorPayload = async (
  response: Response
): Promise<ErrorPayload> => {
  const parsed = await response.json().catch(() => null)

  return parsed && typeof parsed === 'object'
    ? (parsed as ErrorPayload)
    : { error: response.statusText }
}

export const messageForError = (
  errorData: ErrorPayload,
  response: Response,
  errorPrefix: string
): string => {
  const { errors, error } = errorData

  if (Array.isArray(errors)) {
    return errors.map(errorEntryText).join(', ')
  }
  if (typeof errors === 'string' && errors.length > 0) {
    return errors
  }
  if (typeof error === 'string' && error.length > 0) {
    return error
  }

  return `${errorPrefix}: ${response.status} ${response.statusText}`
}

export const errorCodeFrom = ({
  code,
  errors,
}: ErrorPayload): string | undefined => {
  if (typeof code === 'string' && code.length > 0) return code
  if (Array.isArray(errors) && isCodedErrorEntry(errors[0])) {
    return errors[0].code
  }
  return undefined
}
